/* tslint:disable:no-import-side-effect no-require-imports */
import * as THREE from 'three';

import { ObjectUtil } from '../object.util';

import { Scenes } from './three.interface';
import { adenocarcinomaFragmentShader, fragmentShader, vertexShader } from './three.shaders';

export class ThreeUtil {

  public static addObjectToAllScenes = (scenes: Scenes, object: THREE.Object3D): void => {
    ObjectUtil.values(scenes).forEach((scene: THREE.Scene) => scene.add(object));
  }


  public static createShaderMaterial = (uColorVector: THREE.Vector4, isAdenocarcinoma = false) =>
    // Three JS Flat Shading Property is set to false by default, which provides smoothShading by default
    new THREE.ShaderMaterial({
      uniforms: {
        edgefalloff: { type: 'f', value: 1.0 },
        ambient: { type: 'f', value: 0.4 },
        intensity: { type: 'f', value: 1.0 },
        u_color: { type: 'v4', value: uColorVector }
      },
      transparent: true,
      opacity: 0.6,
      vertexShader: vertexShader,
      fragmentShader: isAdenocarcinoma ? adenocarcinomaFragmentShader : fragmentShader,
      depthTest: true,
      depthWrite: false
    });

  public static angleInDegree = (point1: THREE.Vector3,
                                 point2: THREE.Vector3,
                                 camera: THREE.PerspectiveCamera): number => {
    const xDistance = point1.x - point2.x;
    const yDistance = point1.y - point2.y;
    const angleInRadian = Math.max(Math.abs(Math.atan(xDistance / point1.z) / camera.aspect),
      Math.abs(Math.atan(yDistance / point1.z)));
    return angleInRadian / Math.PI * 180;
  }

  public static getCylinderBetweenPoints = (point1: THREE.Vector3,
                                            point2: THREE.Vector3,
                                            material: THREE.Material): THREE.Mesh => {
    const negatedPoint2 = new THREE.Vector3(point2.x, point2.y, point2.z).negate();
    const centerPoint = new THREE.Vector3()
      .subVectors(point1, negatedPoint2)
      .multiplyScalar(0.5);
    const difference = new THREE.Vector3()
      .subVectors(point2, point1);

    const cylinderGeometry = new THREE.CylinderGeometry(1, 1, difference.length(), 12, 1, false);

    const { rotationAxis, angle } = ThreeUtil.calculateAxesAndAngleForCylinder(difference.normalize());

    const translationMatrixForCylinder = ThreeUtil.translationMatrixForCylinder(centerPoint, rotationAxis, angle);
    cylinderGeometry.applyMatrix(translationMatrixForCylinder);
    return new THREE.Mesh(cylinderGeometry, material);
  }

  private static calculateAxesAndAngleForCylinder = (difference: THREE.Vector3) => {
    const zAxis = new THREE.Vector3(0.0, -1.0, 0.0);
    const angle = Math.acos(zAxis.dot(difference));
    const rotationAxis = new THREE.Vector3();
    rotationAxis.crossVectors(zAxis, difference);
    return { rotationAxis, angle };
  }

  private static translationMatrixForCylinder = (centerPoint: THREE.Vector3,
                                                 rotationAxis: THREE.Vector3,
                                                 angle: number): THREE.Matrix4 => {
    const translateMatrix = new THREE.Matrix4().makeTranslation(centerPoint.x, centerPoint.y, centerPoint.z);
    const translation = new THREE.Matrix4().copyPosition(translateMatrix);
    translateMatrix.makeRotationAxis(rotationAxis, angle);
    translateMatrix.copyPosition(translation);
    return translateMatrix;
  }
}
